/**
 * Transition Rules: EnergyPlus 23.1 to 23.2
 *
 * Major changes: District heating rename, crankcase heater curve additions,
 * fuel type standardization
 */

import type { VersionTransition, ObjectTransitionRule, MultiObjectResult, TransitionChange } from '../types';
import type { IDFObject, IDFDocument, IDFField } from '../../parser/types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';
import { copyField, createObject } from '../engine';

/**
 * Transform Coil:Cooling:DX:VariableSpeed - Insert blank field within each speed data block
 *
 * Header fields: 0-11 (before speed data)
 * Per-speed fields: 10 fields per speed, need to insert 1 blank after field 4
 */
function transformCoolingVariableSpeed(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const newFields: IDFField[] = [];
  const objectName = object.fields[0]?.value;

  // Copy header fields (0-11)
  for (let i = 0; i <= 11; i++) {
    if (object.fields[i]) {
      newFields.push(copyField(object.fields[i]));
    }
  }

  // Process per-speed data
  const oldFieldsPerSpeed = 10;
  const speedStartIndex = 12;
  const maxSpeeds = 10; // Variable speed coils can have up to 10 speeds

  for (let speed = 0; speed < maxSpeeds; speed++) {
    const oldStart = speedStartIndex + speed * oldFieldsPerSpeed;
    const hasSpeedData = object.fields[oldStart]?.value.trim();

    if (!hasSpeedData && speed > 0) {
      break; // No more speeds defined
    }

    if (oldStart >= object.fields.length) {
      break;
    }

    // Copy first 5 fields of speed data (indices 0-4)
    for (let i = 0; i < 5; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }

    // Insert new blank field for crankcase heater capacity function curve
    newFields.push({ value: '' });

    // Copy remaining fields of speed data (indices 5-9)
    for (let i = 5; i < oldFieldsPerSpeed; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }
  }

  result.primary = createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  const change: TransitionChange = {
    type: 'field_added',
    objectClass: object.className,
    description: `Added crankcase heater capacity curve name field to each speed data block`,
  };
  if (objectName !== undefined) {
    change.objectName = objectName;
  }
  result.changes.push(change);

  return result;
}

/**
 * Transform Coil:Heating:DX:VariableSpeed - Insert blank field within each speed data block
 */
function transformHeatingVariableSpeed(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const newFields: IDFField[] = [];
  const objectName = object.fields[0]?.value;

  // Copy header fields (0-9)
  for (let i = 0; i <= 9; i++) {
    if (object.fields[i]) {
      newFields.push(copyField(object.fields[i]));
    }
  }

  // Process per-speed data
  const oldFieldsPerSpeed = 5;
  const speedStartIndex = 10;
  const maxSpeeds = 10;

  for (let speed = 0; speed < maxSpeeds; speed++) {
    const oldStart = speedStartIndex + speed * oldFieldsPerSpeed;
    const hasSpeedData = object.fields[oldStart]?.value.trim();

    if (!hasSpeedData && speed > 0) {
      break;
    }

    if (oldStart >= object.fields.length) {
      break;
    }

    // Copy first 4 fields of speed data (indices 0-3)
    for (let i = 0; i < 4; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }

    // Insert new blank field
    newFields.push({ value: '' });

    // Copy remaining field (index 4)
    if (object.fields[oldStart + 4]) {
      newFields.push(copyField(object.fields[oldStart + 4]));
    }
  }

  result.primary = createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  const change: TransitionChange = {
    type: 'field_added',
    objectClass: object.className,
    description: `Added crankcase heater capacity curve name field to each speed data block`,
  };
  if (objectName !== undefined) {
    change.objectName = objectName;
  }
  result.changes.push(change);

  return result;
}

// District heating fuel type mapping
const DISTRICT_HEATING_MAP: Record<string, string> = {
  'DISTRICTHEATING': 'DistrictHeating:Water',
  'STEAM': 'DistrictHeatingSteam',
};

function mapDistrictHeating(value: string): string {
  const upper = value.toUpperCase().replace(/\s+/g, '');
  return DISTRICT_HEATING_MAP[upper] || value;
}

export function createTransition_23_1_to_23_2(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Object rename: DistrictHeating → DistrictHeating:Water
  renamedObjects.set('DISTRICTHEATING', 'DistrictHeating:Water');

  // Coil:Cooling:DX:CurveFit:Performance - Insert blank at position 3
  objectRules.set('COIL:COOLING:DX:CURVEFIT:PERFORMANCE', {
    sourceClass: 'Coil:Cooling:DX:CurveFit:Performance',
    targetClass: 'Coil:Cooling:DX:CurveFit:Performance',
    fieldMappings: [
      ...createCopyMappings(0, 2), // Fields 0-1
      { sourceIndex: -1, targetIndex: 2, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(2, 3, 30),
    ],
  });

  // Coil:Cooling:DX:SingleSpeed - Insert blank at position 27
  objectRules.set('COIL:COOLING:DX:SINGLESPEED', {
    sourceClass: 'Coil:Cooling:DX:SingleSpeed',
    targetClass: 'Coil:Cooling:DX:SingleSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 26), // Fields 0-25
      { sourceIndex: -1, targetIndex: 26, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(26, 27, 20),
    ],
  });

  // Coil:Cooling:DX:MultiSpeed - Insert blank at position 13
  objectRules.set('COIL:COOLING:DX:MULTISPEED', {
    sourceClass: 'Coil:Cooling:DX:MultiSpeed',
    targetClass: 'Coil:Cooling:DX:MultiSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 12), // Fields 0-11
      { sourceIndex: -1, targetIndex: 12, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(12, 13, 80),
    ],
  });

  // Coil:Cooling:DX:TwoStageWithHumidityControlMode - Insert blank at position 6
  objectRules.set('COIL:COOLING:DX:TWOSTAGEWITHHUMIDITYCONTROLMODE', {
    sourceClass: 'Coil:Cooling:DX:TwoStageWithHumidityControlMode',
    targetClass: 'Coil:Cooling:DX:TwoStageWithHumidityControlMode',
    fieldMappings: [
      ...createCopyMappings(0, 5), // Fields 0-4
      { sourceIndex: -1, targetIndex: 5, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(5, 6, 30),
    ],
  });

  // Coil:Heating:DX:SingleSpeed - Insert blank at position 20
  objectRules.set('COIL:HEATING:DX:SINGLESPEED', {
    sourceClass: 'Coil:Heating:DX:SingleSpeed',
    targetClass: 'Coil:Heating:DX:SingleSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 19), // Fields 0-18
      { sourceIndex: -1, targetIndex: 19, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(19, 20, 20),
    ],
  });

  // Coil:Heating:DX:MultiSpeed - Insert blank at position 8
  objectRules.set('COIL:HEATING:DX:MULTISPEED', {
    sourceClass: 'Coil:Heating:DX:MultiSpeed',
    targetClass: 'Coil:Heating:DX:MultiSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 7), // Fields 0-6
      { sourceIndex: -1, targetIndex: 7, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(7, 8, 80),
    ],
  });

  // Coil:WaterHeating:AirToWaterHeatPump:Pumped - Insert blank at position 20
  objectRules.set('COIL:WATERHEATING:AIRTOWATERHEATPUMP:PUMPED', {
    sourceClass: 'Coil:WaterHeating:AirToWaterHeatPump:Pumped',
    targetClass: 'Coil:WaterHeating:AirToWaterHeatPump:Pumped',
    fieldMappings: [
      ...createCopyMappings(0, 19), // Fields 0-18
      { sourceIndex: -1, targetIndex: 19, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(19, 20, 20),
    ],
  });

  // Coil:WaterHeating:AirToWaterHeatPump:Wrapped - Insert blank at position 13
  objectRules.set('COIL:WATERHEATING:AIRTOWATERHEATPUMP:WRAPPED', {
    sourceClass: 'Coil:WaterHeating:AirToWaterHeatPump:Wrapped',
    targetClass: 'Coil:WaterHeating:AirToWaterHeatPump:Wrapped',
    fieldMappings: [
      ...createCopyMappings(0, 12), // Fields 0-11
      { sourceIndex: -1, targetIndex: 12, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(12, 13, 20),
    ],
  });

  // Coil:WaterHeating:AirToWaterHeatPump:VariableSpeed - Insert blank at position 19
  objectRules.set('COIL:WATERHEATING:AIRTOWATERHEATPUMP:VARIABLESPEED', {
    sourceClass: 'Coil:WaterHeating:AirToWaterHeatPump:VariableSpeed',
    targetClass: 'Coil:WaterHeating:AirToWaterHeatPump:VariableSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 18), // Fields 0-17
      { sourceIndex: -1, targetIndex: 18, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(18, 19, 80),
    ],
  });

  // HVACTemplate:Zone:WaterToAirHeatPump - Remove field 29
  objectRules.set('HVACTEMPLATE:ZONE:WATERTOAIRHEATPUMP', {
    sourceClass: 'HVACTemplate:Zone:WaterToAirHeatPump',
    targetClass: 'HVACTemplate:Zone:WaterToAirHeatPump',
    fieldMappings: [
      ...createCopyMappings(0, 28), // Fields 0-27
      { sourceIndex: 28, targetIndex: -1, type: 'delete' },
      ...createShiftedCopyMappings(29, 28, 20),
    ],
  });

  // Objects with district heating fuel type mapping
  const districtHeatingObjects: Array<{ name: string; indices: number[] }> = [
    { name: 'ENERGYMANAGEMENTSYSTEM:METEREDOUTPUTVARIABLE', indices: [4] },
    { name: 'EXTERIOR:FUELEQUIPMENT', indices: [1] },
    { name: 'OTHEREQUIPMENT', indices: [1] },
    { name: 'PYTHONPLUGIN:OUTPUTVARIABLE', indices: [5] },
    { name: 'WATERHEATER:MIXED', indices: [10, 14, 17] },
    { name: 'WATERHEATER:STRATIFIED', indices: [16, 23] },
    { name: 'ZONEHVAC:HYBRIDUNITARYHVAC', indices: [19, 20, 21] },
    { name: 'LIFECYCLECOST:USEPRICEESCALATION', indices: [1] },
    { name: 'LIFECYCLECOST:USEADJUSTMENT', indices: [1] },
  ];

  for (const obj of districtHeatingObjects) {
    const maxIndex = Math.max(...obj.indices) + 10;
    const mappings = [];
    for (let i = 0; i <= maxIndex; i++) {
      if (obj.indices.includes(i)) {
        mappings.push({
          sourceIndex: i,
          targetIndex: i,
          type: 'transform' as const,
          transform: mapDistrictHeating,
        });
      } else {
        mappings.push({ sourceIndex: i, targetIndex: i, type: 'copy' as const });
      }
    }
    objectRules.set(obj.name, {
      sourceClass: obj.name,
      targetClass: obj.name,
      fieldMappings: mappings,
    });
  }

  // Coil:Cooling:DX:VariableSpeed - Per-speed field insertion
  objectRules.set('COIL:COOLING:DX:VARIABLESPEED', {
    sourceClass: 'Coil:Cooling:DX:VariableSpeed',
    targetClass: 'Coil:Cooling:DX:VariableSpeed',
    fieldMappings: [],
    multiObjectTransform: transformCoolingVariableSpeed,
  });

  // Coil:Heating:DX:VariableSpeed - Per-speed field insertion
  objectRules.set('COIL:HEATING:DX:VARIABLESPEED', {
    sourceClass: 'Coil:Heating:DX:VariableSpeed',
    targetClass: 'Coil:Heating:DX:VariableSpeed',
    fieldMappings: [],
    multiObjectTransform: transformHeatingVariableSpeed,
  });

  return {
    fromVersion: { major: 23, minor: 1 },
    toVersion: { major: 23, minor: 2 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
